import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@navikt/aksel-icons';
import { useGetOrganizationUsageStatsQuery } from '../../../hooks/queries/useGetOrganizationUsageStatsQuery';
import { useExportOrganizationUsagePdfMutation } from '../../../hooks/mutations/useExportOrganizationUsagePdfMutation';
import { Alert, Button, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { OrganizationLayout } from '../../../components/OrganizationLayout';
import { DateRangePicker, type DateRange } from '../../../components/DateRangePicker/DateRangePicker';
import { DateRangeCalculator } from './utils/DateRangeCalculator';
import { UsageStatsTransformer } from './utils/UsageStatsTransformer';
import { TeamUsageSorter } from './utils/TeamUsageSorter';
import { PrimaryStatCard } from './components/PrimaryStatCard';
import { SecondaryStatCard } from './components/SecondaryStatCard';
import { AutomationStatsCard } from './components/AutomationStatsCard';
import { TeamUsageTable } from './components/TeamUsageTable';
import { TeamUsageCards } from './components/TeamUsageCards';
import { OrganizationUsagePdfWrapper } from './components/OrganizationUsagePdfWrapper';
import { DownloadBlobFileUtils } from '../../../utils/DownloadBlobFileUtils';
import classes from './OrganizationUsagePage.module.css';

const getInitialDateRange = (): DateRange => {
  const params = new URLSearchParams(window.location.search);
  const periodStart = params.get('periodStart');
  const periodEnd = params.get('periodEnd');

  if (periodStart && periodEnd) {
    return {
      fromDate: periodStart,
      toDate: periodEnd,
    };
  }

  return DateRangeCalculator.getLast90Days();
};

export const OrganizationUsagePage = () => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange());

  const {
    data: usageStats,
    error,
    isLoading,
  } = useGetOrganizationUsageStatsQuery(dateRange.fromDate, dateRange.toDate);

  const exportPdfMutation = useExportOrganizationUsagePdfMutation();

  const sortedTeamUsageStats = useMemo(() => {
    if (!usageStats) return [];
    return TeamUsageSorter.sortByTotalSurveys(usageStats.teamUsageStats);
  }, [usageStats]);

  // Always wrap content in PDF wrapper for export to work
  const content = () => {
    if (error) {
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {error.message}
          </Heading>
          <Paragraph>{t('usage.overview.error.message')}</Paragraph>
        </Alert>
      );
    }

    if (isLoading) {
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t('usage.overview.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={320} />
        </div>
      );
    }

    if (!usageStats) {
      return null;
    }

    const primaryStats = UsageStatsTransformer.transformPrimaryStats(usageStats, t);
    const secondaryStats = UsageStatsTransformer.transformSecondaryStats(usageStats, t);

    return (
      <div className={classes.container} data-testid="organization-usage-content">
        {/* Tittelen står i organisasjonstoppen på skjerm, men trengs i PDF-en */}
        <div className={classes.headerContent} data-print="only">
          <span className={classes.sectionLabel}>{t('usage.overview.sectionLabel')}</span>
          <h1 className={classes.pageTitle}>{t('usage.overview.pageTitle')}</h1>
        </div>

        <div className={classes.header} data-print="false">
          <DateRangePicker
            names={{ from: 'periodStart', to: 'periodEnd' }}
            values={{ from: new Date(dateRange.fromDate), to: new Date(dateRange.toDate) }}
            setDateRange={setDateRange}
          />
          <Button
            variant="secondary"
            onClick={handleExportPdf}
            disabled={exportPdfMutation.isPending}
            data-testid="export-button"
            data-print="false"
          >
            <FilePdfIcon aria-hidden />
            {exportPdfMutation.isPending ? t('usage.overview.pdf.exporting') : t('usage.overview.pdf.exportButton')}
          </Button>
        </div>

        <ul className={classes.primaryStats}>
          {primaryStats.map((stat) => (
            <PrimaryStatCard key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} />
          ))}
        </ul>

        <section aria-labelledby="usage-surveys-heading" className={classes.panel}>
          <div className={classes.panelHeader}>
            <h2 id="usage-surveys-heading" className={classes.sectionTitle}>
              {t('usage.overview.surveysTitle')}
            </h2>
          </div>
          <ul className={classes.secondaryStatsGrid}>
            {secondaryStats.map((stat) => (
              <SecondaryStatCard key={stat.label} label={stat.label} value={stat.value as number} total={stat.total} />
            ))}
          </ul>
        </section>

        <AutomationStatsCard
          stats={usageStats.automationStats}
          translations={{
            title: t('usage.overview.automation.title'),
            description: t('usage.overview.automation.description'),
            remindersSent: t('usage.overview.automation.remindersSent'),
            deadlineExtensions: t('usage.overview.automation.deadlineExtensions'),
            resultsShared: t('usage.overview.automation.resultsShared'),
            actionPlanInvitations: t('usage.overview.automation.actionPlanInvitations'),
            actionPlanNotifications: t('usage.overview.automation.actionPlanNotifications'),
            aiRecommendationsGenerated: t('usage.overview.automation.aiRecommendationsGenerated'),
          }}
        />

        {/* Team Breakdown - Table for web, Cards for PDF */}
        <section aria-labelledby="usage-teams-heading" className={`${classes.panel} ${classes.tableSection}`}>
          <div className={classes.panelHeader}>
            <h2 id="usage-teams-heading" className={classes.sectionTitle}>
              {t('usage.overview.teamBreakdown')}
            </h2>
          </div>

          {/* Table view - visible on web, hidden in PDF */}
          <div className={classes.tableContainer} data-print="false">
            <TeamUsageTable teams={sortedTeamUsageStats} />
          </div>

          {/* Card view - hidden on web, visible in PDF */}
          <div data-print="only">
            <TeamUsageCards
              teams={sortedTeamUsageStats}
              translations={{
                teamSize: t('usage.overview.table.teamSize'),
                channels: t('usage.overview.table.channels'),
                surveys: t('usage.overview.table.surveys'),
                responses: t('usage.overview.table.responses'),
                responseRate: t('usage.overview.table.responseRate'),
                nextScheduled: t('usage.overview.table.nextScheduled'),
                scheduled: t('usage.overview.table.scheduled'),
                lastSurvey: t('usage.overview.table.lastSurvey'),
                noChannels: t('usage.overview.table.noChannels'),
                noSurveys: t('usage.overview.table.noSurveys'),
                recurring: t('usage.overview.table.recurring'),
                members: t('usage.overview.table.members'),
                activity: t('usage.overview.table.activity'),
                engagement: t('usage.overview.table.engagement'),
              }}
            />
          </div>
        </section>
      </div>
    );
  };

  const handleExportPdf = async () => {
    try {
      const blob = await exportPdfMutation.mutateAsync({
        periodStart: dateRange.fromDate,
        periodEnd: dateRange.toDate,
      });

      const filename = DownloadBlobFileUtils.generateFilenameWithDate(
        'Trustpulse-Organisasjonsstatistikk',
        'pdf',
      );

      DownloadBlobFileUtils.downloadBlob(blob, filename);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert(t('usage.overview.pdf.exportError'));
    }
  };

  return (
    <OrganizationUsagePdfWrapper
      periodStart={dateRange.fromDate ? new Date(dateRange.fromDate) : null}
      periodEnd={dateRange.toDate ? new Date(dateRange.toDate) : null}
      isReady={!isLoading && !!usageStats}
    >
      <OrganizationLayout>{content()}</OrganizationLayout>
    </OrganizationUsagePdfWrapper>
  );
};
